import React from 'react';
import { observer } from 'mobx-react';
import { Table } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { ICallDataRecordsListItem, IGetApiType, ITableInclude } from '../../../interfaces';
import {
    useCallDataRecordsList,
    usePagesStoreListRight,
    useProfile,
    useProfileIsRight,
    useProfileSettings,
    useUiIsSpinnerFound,
    useUiSearch,
} from '../../../hooks';
import { lightText, smsRange } from '../../../instruments';
import { tF, tI } from '../../../translate';
import {
    Catcher,
    LinkComponent,
    MetadataCollapse,
    ShowDataBytes,
    ShowVoice,
    TableItemDeleteColumn,
    TableItemToolbar,
    columnDeleted,
    columnToolbar,
} from '../../../Components';
import Styles from '../../styles/m_viewStyles.less';

export const CallDataRecordsTableList: React.FC<ITableInclude> = observer(({ isIncluded }: ITableInclude) => {
    const list = useCallDataRecordsList();
    const right = usePagesStoreListRight('CallDataRecordsList');
    const isLord = useProfileIsRight('LORD');
    const { isCompanyTypeIdMNOHost } = useProfile();
    const { hideCosts } = useProfileSettings();

    const loadingTypes: IGetApiType[] = ['GET_CALL_DATA_RECORDS_LIST', 'GET_CALL_DATA_RECORDS_LAST_5'];
    const isLoading = useUiIsSpinnerFound(loadingTypes);
    const search = useUiSearch();

    const tableColumns = [
        !isCompanyTypeIdMNOHost
            ? {
                  title: tF('Card Name'),
                  dataIndex: 'cardNameLink',
              }
            : {},
        {
            title: tF('Card'),
            dataIndex: 'cardShortIdLink',
        },
        {
            title: tF('Operator'),
            dataIndex: 'operatorNameLink',
        },
        {
            title: tF('Country'),
            dataIndex: 'countryLink',
        },
        {
            title: tF('Usage'),
            dataIndex: 'showUnits',
            className: Styles.columnRight,
        },
        hideCosts
            ? {}
            : {
                  title: tF('Cost'),
                  dataIndex: 'cost',
                  className: Styles.columnRight,
              },
        {
            title: tF('Start Date'),
            dataIndex: 'startDate',
            width: 180,
            className: Styles.columnRight,
        },
        columnDeleted(isLord),
        !isIncluded ? columnToolbar : {},
    ];

    const tableData = list.map((item: ICallDataRecordsListItem) => {
        return {
            key: item.id,
            ...item,
            cardShortIdLink: (
                <LinkComponent currentListComponent={'CardList'} id={item.cardId} isLight name={item.cardShortId} />
            ),
            cardNameLink: (
                <LinkComponent currentListComponent={'CardList'} id={item.cardId} isLight name={item.cardName} />
            ),
            operatorNameLink: (
                <LinkComponent
                    currentListComponent={'OperatorsList'}
                    id={item.operatorId}
                    isLight
                    name={item.operatorName}
                />
            ),
            countryLink: (
                <LinkComponent
                    currentListComponent={'CountryList'}
                    id={item.countryId}
                    isLight
                    name={item.countryName}
                />
            ),
            showUnits:
                item.source === 'data' ? (
                    <ShowDataBytes bytes={item?.units ? +item.units : undefined} isHumanView />
                ) : item.source === 'sms' ? (
                    smsRange(item?.units)
                ) : item.source === 'voice' ? (
                    <ShowVoice valueSec={item?.units} />
                ) : (
                    item?.units
                ),
            deleted: <TableItemDeleteColumn deletedAt={item.deletedAt} />,
            toolbar: <TableItemToolbar id={item.id} itemName={`"${item.id}" ${tI('Call Data Record')}`} {...right} />,
        };
    });

    const expandedRowRender = (record: ICallDataRecordsListItem): JSX.Element => {
        return (
            <div className={Styles.expandedRow}>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('Card Set')}</div>
                    <div className={Styles.expandedRowLineValue}>
                        <LinkComponent
                            currentListComponent={'CardSetsList'}
                            id={record.cardSetId}
                            isLight
                            name={record.cardSetName}
                        />
                    </div>
                </div>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('PLMN')}</div>
                    <div className={Styles.expandedRowLineValue}>
                        <LinkComponent
                            currentListComponent={'CardList'}
                            id={record.cardId}
                            isLight
                            name={record.cardPlmnList}
                        />
                    </div>
                </div>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('Operator')}</div>
                    <div className={Styles.expandedRowLineValue}>
                        <LinkComponent
                            currentListComponent={'OperatorsList'}
                            id={record.operatorId}
                            isLight
                            name={record.operatorName}
                        />
                    </div>
                </div>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('IMSI')}</div>
                    <div className={Styles.expandedRowLineValue}>
                        <LinkComponent
                            currentListComponent={'SubscribersList'}
                            id={record.subscriberId}
                            isLight
                            name={record.imsi}
                        />
                    </div>
                </div>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('Visited Network')}</div>
                    <div className={Styles.expandedRowLineValue}>
                        <LinkComponent
                            currentListComponent={'OperatorsList'}
                            id={record.visitedNetworkId}
                            isLight
                            name={record.visitedNetworkName}
                        />
                    </div>
                </div>
                {isLord ? (
                    <div className={Styles.expandedRowLine}>
                        <div className={Styles.expandedRowLineLabel}>{tF('CDR operator ID')}</div>
                        <div className={Styles.expandedRowLineValue}>
                            {/*<LinkComponent*/}
                            {/*    currentListComponent={'OperatorsList'}*/}
                            {/*    id={record.operatorCdrId}*/}
                            {/*    isLight*/}
                            {/*    name={record.operatorCdrId}*/}
                            {/*/>*/}
                            {lightText(record.operatorCdrId || '', search)}
                        </div>
                    </div>
                ) : null}
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('Source')}</div>
                    <div className={Styles.expandedRowLineValue}>{record.source}</div>
                </div>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('End')}</div>
                    <div className={Styles.expandedRowLineValue}>{record.endDate}</div>
                </div>
                {record.billedAsPackage ? (
                    <div className={Styles.expandedRowLine}>
                        <div className={Styles.expandedRowLineLabel}>{tF('Billed as package')}</div>
                        <div className={Styles.expandedRowLineValue}>
                            <CheckOutlined />
                        </div>
                    </div>
                ) : null}
                <MetadataCollapse metadata={record.metadata} />
            </div>
        );
    };

    const expandable = {
        expandedRowRender: expandedRowRender,
    };

    return (
        <Catcher>
            <div className={Styles.tablePlace}>
                <Table
                    className={'table-theme'}
                    columns={tableColumns}
                    dataSource={tableData}
                    expandable={expandable}
                    loading={isLoading}
                    pagination={false}
                    size={'small'}
                />
            </div>
        </Catcher>
    );
});
